import React, { useState } from "react";
import { Instagram, Sparkles, Trophy, Shield, Save, Globe } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

const ITEMS = [
  {
    key: "allow_spotlight_share",
    icon: Instagram,
    title: "Allow sharing of my Spotlight card",
    description:
      "If you're picked as Spotlight of the Month, let colleagues share your Instagram card externally. You can still view it privately either way.",
  },
  {
    key: "allow_badge_share",
    icon: Sparkles,
    title: "Allow sharing of my badges",
    description:
      "Let you (and any admin) download a shareable card for each badge you unlock, to post on Instagram or elsewhere.",
  },
  {
    key: "show_on_leaderboard",
    icon: Trophy,
    title: "Show me on the public Leaderboard",
    description:
      "Be included in the Leaderboard rankings. Disable if you'd rather stay private. Your points are still tracked — they just won't be shown publicly.",
  },
  {
    key: "allow_public_profile",
    icon: Globe,
    title: "Public trophy page",
    description:
      "Generate a no-login profile page (badges, hires, total earned, year joined) at a shareable link — perfect for LinkedIn bios. Disable to make your profile private.",
  },
];

export default function PermissionsForm({ onSaved, submitLabel = "Save preferences", compact = false }) {
  const { user, refreshMe } = useAuth();
  const initial = {
    allow_spotlight_share: true,
    allow_badge_share: true,
    show_on_leaderboard: true,
    allow_public_profile: true,
    ...(user?.permissions || {}),
  };
  const [values, setValues] = useState(initial);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/auth/me/permissions", values);
      await refreshMe();
      toast.success("Preferences saved");
      if (onSaved) onSaved(values);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const toggle = (k) => setValues((v) => ({ ...v, [k]: !v[k] }));

  return (
    <div className="space-y-4">
      {ITEMS.map((item) => {
        const Icon = item.icon;
        const on = !!values[item.key];
        return (
          <div
            key={item.key}
            className={`border-2 border-black rounded-2xl p-4 flex items-start gap-4 ${on ? "bg-brand-yellow" : "bg-white"} ${compact ? "" : "shadow-brutal"}`}
            data-testid={`perm-item-${item.key}`}
          >
            <div className="w-10 h-10 border-2 border-black rounded-xl bg-white flex items-center justify-center flex-shrink-0">
              <Icon className="w-5 h-5" strokeWidth={2.3} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-black text-lg leading-tight">{item.title}</div>
              <div className="text-sm text-neutral-700 mt-1">{item.description}</div>
            </div>
            <button
              type="button"
              onClick={() => toggle(item.key)}
              aria-pressed={on}
              className={`relative w-14 h-8 border-2 border-black rounded-full flex-shrink-0 transition-colors ${on ? "bg-brand-green" : "bg-white"}`}
              data-testid={`perm-toggle-${item.key}`}
            >
              <span
                className={`absolute top-[2px] w-6 h-6 bg-white border-2 border-black rounded-full transition-all ${
                  on ? "left-[30px]" : "left-[2px]"
                }`}
              />
            </button>
          </div>
        );
      })}

      <button
        onClick={save}
        disabled={busy}
        className="btn-brutal btn-primary w-full py-3 text-base"
        data-testid="perm-save"
      >
        <Save className="w-4 h-4" /> {busy ? "Saving…" : submitLabel}
      </button>

      <div className="flex items-start gap-2 text-xs text-neutral-600 pt-1">
        <Shield className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>
          You can change any of these from <strong>Settings</strong> at any time. Nothing here affects the points you've already earned.
        </span>
      </div>
    </div>
  );
}
